import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { MyDispatch, MySelector } from "../../redux/store"
import { toast } from "sonner"
import { Box, Modal } from "@mui/material"
import Loader from "../utils/Loader"
import { setSwitchRoleModal } from "../../redux/utilSlice"
import { HandleGalleryFns } from "../../hooks/handleGalleryFns"
import {
  btnsParent,
  desc,
  leftBtn,
  returnStyle,
  rightBtn,
  title,
} from "../../utils/popupModalStyle"
import { getDesignFontClass, getDesignCasingClass, getDesignModalSize } from "../../gallery-designs/registry"
import { safeSessionStorage } from "../../utils/safeStorage"

const SwitchRoleModal = () => {
  const dispatch = useDispatch<MyDispatch>()
  
  const { switchRoleModal } = MySelector((state) => state.util)
  const { config, loading, data } = MySelector((state) => state.event)
  // Modals render in a portal, so apply the design font directly.
  const fontClass = getDesignFontClass(data?.galleryTheme)
  const casingClass = getDesignCasingClass(data?.galleryTheme)
  const modalSize = getDesignModalSize(data?.galleryTheme)
  const { fullScreenId } = MySelector((state) => state.util)
  const { theme } = MySelector((state) => state.theme)
  const { triggerFullScreen } = HandleGalleryFns()

  const [pin, setPin] = useState<string>("")
  const [showModal, setShowModal] = useState(false)

  // to toggle the modal
  useEffect(() => {
    if (!switchRoleModal) setShowModal(false)
    else setShowModal(true)
  }, [switchRoleModal])

  const currentRole = safeSessionStorage.getItem(process.env.REACT_APP_ACTIVE_SESSION || "")

  const handleRoleSwitch = () => {
    if (currentRole === "host") {
      // Switching from host to guest
      safeSessionStorage.setItem(process.env.REACT_APP_ACTIVE_SESSION || "", "guest")
      toast.success("Switched to Guest mode")
      setShowModal(false)
      dispatch(setSwitchRoleModal(false))

      // Redirect to current page to refresh permissions
      if (fullScreenId) {
        triggerFullScreen()
      } else {
        window.location.reload()
      }
    } else {
      // Switching from guest to host, need to verify pin
      if (!config) return
      if (pin === "") return toast.info("Please enter your pin")
      if (pin === config?.pincode) {
        safeSessionStorage.setItem(process.env.REACT_APP_ACTIVE_SESSION || "", "host")
        toast.success("Switched to Host mode")
        setShowModal(false)
        dispatch(setSwitchRoleModal(false))
        
        // Redirect to current page to refresh permissions
        if (fullScreenId) {
          triggerFullScreen()
        } else {
          window.location.reload()
        }
      } else {
        toast.error("Wrong Pin Provided")
        setPin("")
      }
    }
  }

  return (
    <Modal
      open={showModal}
      onClose={() => {
        setShowModal(false)
        dispatch(setSwitchRoleModal(false))
        setPin("")
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={returnStyle(theme)}>
        <div className={`p-5 md:px-[26.11px] md:py-[33.25px] ${fontClass}`}>
          <p className={`${title} ${casingClass} ${modalSize.title}`}>
            {currentRole === "host"
              ? "Switch to Guest Mode"
              : "Switch to Host Mode"}
          </p>
          <p className={`${desc} ${casingClass} ${modalSize.desc}`} >
            {currentRole === "guest" 
              ? "Enter the pin code to switch to host mode."
              : "Are you sure you want to switch to guest mode?"}
          </p>

          {currentRole === "guest" && (
            <div className="mt-4 mb-4">
              <input
                type="number"
                placeholder="Enter 4 digit code"
                value={pin}
                max={4}
                maxLength={4}
                autoFocus
                onChange={(e) => setPin(e.target.value)}
                className="w-full bg-transparent border-[0.5px] border-offGrey/80 focus:border-offWhite placeholder:text-offGrey text-brightBlack dark:text-offWhite font-[400] text-sm leading-[19.36px] outline-none rounded-2xl py-[12px] px-[16px]"
                onKeyDown={(e: any) => {
                  if (e.key !== "Backspace" && pin.length === 4) e.preventDefault()
                  if (e.key === "Enter" && pin !== "") handleRoleSwitch()
                }}
              />
            </div>
          )}

          <div className={btnsParent}>
            <button
              onClick={() => {
                setShowModal(false)
                dispatch(setSwitchRoleModal(false))
                setPin("")
              }}
              className={`${leftBtn} ${casingClass} ${modalSize.button}`}
            >
              Cancel
            </button>
            <button
              onClick={handleRoleSwitch}
              className={`${rightBtn} ${casingClass} ${modalSize.button} ${
                currentRole === "host" || pin.length === 4
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              } flex justify-center items-center`}
              disabled={currentRole !== "host" && pin.length !== 4}
            >
              {loading ? <Loader /> : "Confirm"}
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  )
}

export default SwitchRoleModal